// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

// ──────────────────────────────────────────────
// BRAND
// ──────────────────────────────────────────────
const APP_BRAND   = 'Florigen';
const APP_TAGLINE = 'NFT Generator';

// ──────────────────────────────────────────────
// ADMIN TEAM — Overwatch access
// ──────────────────────────────────────────────
const ADMIN_TEAM = [
  'J.lutz@miskulintechnologies.com',
  'R.miskulin@miskulintechnologies.com',
];

// ──────────────────────────────────────────────
// SVG ICON SET — monochrome, 16x16, stroke-based
// ──────────────────────────────────────────────
const IC = {
  dashboard:  <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="9" y="1.5" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="1.5" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/><rect x="9" y="9" width="5.5" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.2"/></svg>,
  generator:  <svg viewBox="0 0 16 16" fill="none"><path d="M1.5 4.5l6.5-3 6.5 3v7l-6.5 3-6.5-3v-7z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><polyline points="1.5,4.5 8,7.5 14.5,4.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><line x1="8" y1="7.5" x2="8" y2="14.5" stroke="currentColor" strokeWidth="1.2"/></svg>,
  gallery:    <svg viewBox="0 0 16 16" fill="none"><rect x="1.5" y="1.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="6" y="1.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="10.5" y="1.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="1.5" y="6" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="6" y="6" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="10.5" y="6" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="1.5" y="10.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="6" y="10.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="10.5" y="10.5" width="4" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.2"/></svg>,
  mint:       <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/><path d="M5.5 8h5M8 5.5v5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 1.5"/></svg>,
  overwatch:  <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2"/><line x1="8" y1="2" x2="8" y2="4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><line x1="8" y1="12" x2="8" y2="14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><line x1="2" y1="8" x2="4" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><line x1="12" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  analytics:  <svg viewBox="0 0 16 16" fill="none"><rect x="2" y="8" width="3" height="6" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="6.5" y="5" width="3" height="9" rx="0.5" stroke="currentColor" strokeWidth="1.2"/><rect x="11" y="2" width="3" height="12" rx="0.5" stroke="currentColor" strokeWidth="1.2"/></svg>,
  settings:   <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.2"/><path d="M8 1v1.5M8 13.5V15M1 8h1.5M13.5 8H15M3.05 3.05l1.06 1.06M11.89 11.89l1.06 1.06M12.95 3.05l-1.06 1.06M4.11 11.89l-1.06 1.06" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>,
  guide:      <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.2"/><path d="M6.5 6c0-1 .7-1.5 1.5-1.5s1.5.7 1.5 1.5c0 1-1.5 1.5-1.5 2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><circle cx="8" cy="11" r="0.7" fill="currentColor"/></svg>,
};

// ──────────────────────────────────────────────
// PATH → ICON MAP
// ──────────────────────────────────────────────
const PATH_TO_ICON = {
  '/dashboard':  'dashboard',
  '/generator':  'generator',
  '/gallery':    'gallery',
  '/mint':       'mint',
  '/overwatch':  'overwatch',
  '/analytics':  'analytics',
  '/profile':    'settings',
};

// ──────────────────────────────────────────────
// NAV ITEMS — Role-based
// ──────────────────────────────────────────────
const NAV_ITEMS = {
  owner: [
    { section: 'Create' },
    { path: '/generator', label: 'Generator' },
    { path: '/gallery',   label: 'Gallery' },
    { section: 'Mint' },
    { path: '/mint',      label: 'Mint NFT' },
    { section: 'Command' },
    { path: '/overwatch',  label: 'Overwatch' },
    { path: '/dashboard',  label: 'My Collection' },
    { path: '/analytics',  label: 'Analytics' },
    { section: 'System' },
    { path: '/profile',    label: 'Settings' },
  ],
  admin: [
    { section: 'Create' },
    { path: '/generator', label: 'Generator' },
    { path: '/gallery',   label: 'Gallery' },
    { section: 'Mint' },
    { path: '/mint',      label: 'Mint NFT' },
    { section: 'Command' },
    { path: '/overwatch',  label: 'Overwatch' },
    { path: '/dashboard',  label: 'My Collection' },
    { path: '/analytics',  label: 'Analytics' },
    { section: 'System' },
    { path: '/profile',    label: 'Settings' },
  ],
  creator: [
    { section: 'Create' },
    { path: '/generator', label: 'Generator' },
    { path: '/gallery',   label: 'Gallery' },
    { section: 'Mint' },
    { path: '/mint',      label: 'Mint NFT' },
    { section: 'Collection' },
    { path: '/dashboard',  label: 'My Collection' },
    { section: 'System' },
    { path: '/profile',    label: 'Settings' },
  ],
  viewer: [
    { section: 'Browse' },
    { path: '/generator', label: 'Generator' },
    { path: '/gallery',   label: 'Gallery' },
    { section: 'System' },
    { path: '/profile',    label: 'Settings' },
  ],
};

// ──────────────────────────────────────────────
// ROLE RESOLVER
// ──────────────────────────────────────────────
function getEffectiveRole(user) {
  if (!user) return 'viewer';
  const email = (user.email || '').toLowerCase();
  if (ADMIN_TEAM.map(e => e.toLowerCase()).includes(email)) return 'owner';
  const meta = user.user_metadata || {};
  return meta.role || 'creator';
}

function isAdminTeam(email) {
  if (!email) return false;
  return ADMIN_TEAM.map(e => e.toLowerCase()).includes(email.toLowerCase());
}

// ──────────────────────────────────────────────
// LAYOUT COMPONENT
// ──────────────────────────────────────────────
export default function Layout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewAsRole, setViewAsRole] = useState(null);

  // Close sidebar on route change
  React.useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Body class for sidebar state
  React.useEffect(() => {
    if (sidebarOpen) document.body.classList.add('sidebar-open');
    else document.body.classList.remove('sidebar-open');
    return () => document.body.classList.remove('sidebar-open');
  }, [sidebarOpen]);

  const actualRole = getEffectiveRole(user);
  const role = viewAsRole || actualRole;
  let navItems = NAV_ITEMS[role] || NAV_ITEMS.creator;

  // Hide Overwatch from non-admins
  if (!isAdminTeam(user?.email)) {
    navItems = navItems.filter(i => i.path !== '/overwatch');
  }

  const initials = user?.user_metadata?.full_name?.split(' ').map(n => n[0]).join('')
    || user?.email?.charAt(0)?.toUpperCase()
    || 'U';
  const displayName = user?.user_metadata?.full_name || user?.email || 'User';

  return (
    <div className="app-layout" style={{ flexDirection: 'column' }}>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Mobile hamburger */}
        <button
          className="mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={sidebarOpen}
        >
          {sidebarOpen ? '\u2715' : '\u2630'}
        </button>

        {/* Sidebar overlay for mobile */}
        {sidebarOpen && (
          <div
            className="sidebar-overlay"
            role="presentation"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside
          className={`sidebar ${sidebarOpen ? 'open' : ''}`}
          role="navigation"
          aria-label="Main navigation"
        >
          {/* Header */}
          <div className="sidebar-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
              <div style={{
                width: 28, height: 28, borderRadius: 6,
                background: 'linear-gradient(135deg, #7C3AED, #A78BFA)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 700, color: '#fff',
              }}>
                F
              </div>
              <div style={{ flex: 1 }}>
                <div className="sidebar-logo">{APP_BRAND}</div>
                <div className="sidebar-sub">{APP_TAGLINE}</div>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="sidebar-nav" aria-label="Primary navigation">
            {navItems.map((item, i) =>
              item.section ? (
                <div key={i} className="sidebar-section">{item.section}</div>
              ) : (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <span className="nav-icon" style={{ display: 'inline-flex', alignItems: 'center', width: 16, height: 16 }}>
                    {IC[PATH_TO_ICON[item.path]] || IC.settings}
                  </span>
                  {item.label}
                </NavLink>
              )
            )}

            {/* View As Role — owner only */}
            {actualRole === 'owner' && (
              <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <div style={{
                  fontSize: 9, textTransform: 'uppercase', letterSpacing: '1.5px',
                  color: 'var(--text-muted)', marginBottom: 8, fontWeight: 700,
                }}>
                  View As Role
                </div>
                {['admin', 'creator', 'viewer'].map(r => (
                  <button
                    key={r}
                    onClick={() => setViewAsRole(viewAsRole === r ? null : r)}
                    style={{
                      display: 'block', width: '100%', padding: '6px 10px', marginBottom: 3,
                      background: viewAsRole === r ? 'rgba(124, 58, 237, 0.1)' : 'transparent',
                      color: viewAsRole === r ? '#A78BFA' : 'var(--text-steel)',
                      border: `1px solid ${viewAsRole === r ? 'rgba(124, 58, 237, 0.3)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer',
                      textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.8px',
                      fontFamily: 'inherit', fontWeight: 500,
                    }}
                  >
                    {viewAsRole === r ? '\u2713 ' : ''}{r.charAt(0).toUpperCase() + r.slice(1)}
                  </button>
                ))}
                {viewAsRole && (
                  <button
                    onClick={() => setViewAsRole(null)}
                    style={{
                      display: 'block', width: '100%', padding: '6px 10px', marginTop: 6,
                      background: 'transparent', color: 'var(--error-text)',
                      border: '1px solid rgba(140,58,58,0.3)',
                      borderRadius: 'var(--radius-sm)', fontSize: 11, cursor: 'pointer',
                      textAlign: 'center', fontWeight: 600, letterSpacing: '0.8px',
                      fontFamily: 'inherit', textTransform: 'uppercase',
                    }}
                  >
                    \u2190 Back to Owner
                  </button>
                )}
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="sidebar-footer">
            <div className="user-info">
              <div className="user-avatar" style={{ background: 'linear-gradient(135deg, #7C3AED, #A78BFA)' }}>
                {initials}
              </div>
              <div>
                <div className="user-name">{displayName}</div>
                <div className="user-role">
                  {viewAsRole ? `Viewing as: ${viewAsRole}` : role}
                </div>
              </div>
            </div>
            <div style={{ height: 8 }} />
            <button
              className="btn btn-outline btn-sm"
              style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}
              onClick={() => { signOut(); navigate('/login'); }}
              aria-label="Sign out of your account"
            >
              Sign Out
            </button>
            <div style={{
              padding: '12px 0 0', textAlign: 'center', fontSize: 10,
              color: 'var(--text-muted)', borderTop: '1px solid var(--border)', marginTop: 12,
            }}>
              <a href="/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Privacy</a>
              {' \u00B7 '}
              <a href="/terms" style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>Terms</a>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="main-content" role="main" aria-label="Page content">
          <Outlet context={{ effectiveRole: role, actualRole, viewAsRole }} />
        </main>
      </div>
    </div>
  );
}
